import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { ShippingLineService } from 'src/app/services/shipping-line/shipping-line.service';
import { ShippingLineModalService } from 'src/app/services/shipping-line-modal/shipping-line-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-shipping-line',
  templateUrl: './shipping-line.component.html',
  styleUrls: ['./shipping-line.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class ShippingLineComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  shippingLines: any[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedShippingLines: string[] = [];

  constructor(
    private _shippingLineService: ShippingLineService,
    private _shippingLineModalService: ShippingLineModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadShippingLines();
  }

  ngAfterViewInit(): void {
    // No longer needed since we removed dropdown menus
  }

  loadShippingLines(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;
    this.selectedShippingLines = []; // Clear selections when loading new data
    this.searchTerm = search;
    this.filters = filters;

    this.subscriptions.push(
      this._shippingLineService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.shippingLines = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.shippingLines = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        },
        error: (error) => {
          console.error('Error loading shipping lines:', error);
          this.shippingLines = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadShippingLines(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadShippingLines(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadShippingLines();
  }

  openAddShippingLineModal(): void {
    this._shippingLineModalService.clearSelectedShippingLine();
    const modalElement = document.getElementById('kt_modal_shipping_line_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(shippingLine: any): void {
    this._shippingLineModalService.setSelectedShippingLine(shippingLine);
    const modalElement = document.getElementById('kt_modal_shipping_line_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteShippingLine(shippingLine: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${shippingLine.name}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(shippingLine);
      }
    });
  }

  private performDelete(shippingLine: any): void {
    const shippingLineId = shippingLine._id || shippingLine.id;
    this.subscriptions.push(
      this._shippingLineService.DeleteDataById(shippingLineId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Shipping line has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadShippingLines(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete shipping line.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting shipping line.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Event handlers for modal events
  onShippingLineAdded(): void {
    console.log('Shipping line added, refreshing list...');
    this.loadShippingLines(this.currentPage, this.searchTerm, this.filters);
  }

  onShippingLineUpdated(): void {
    console.log('Shipping line updated, refreshing list...');
    this.loadShippingLines(this.currentPage, this.searchTerm, this.filters);
  }

  // Utility methods
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  // Bulk selection methods
  isSelected(shippingLineId: string): boolean {
    return this.selectedShippingLines.includes(shippingLineId);
  }

  isAllSelected(): boolean {
    return this.shippingLines.length > 0 && this.selectedShippingLines.length === this.shippingLines.length;
  }

  isIndeterminate(): boolean {
    return this.selectedShippingLines.length > 0 && this.selectedShippingLines.length < this.shippingLines.length;
  }

  toggleSelection(shippingLineId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedShippingLines.includes(shippingLineId)) {
        this.selectedShippingLines.push(shippingLineId);
      }
    } else {
      this.selectedShippingLines = this.selectedShippingLines.filter(id => id !== shippingLineId);
    }
    this.toggleToolbars();
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedShippingLines = this.shippingLines.map(shippingLine => shippingLine._id);
    } else {
      this.selectedShippingLines = [];
    }
    this.toggleToolbars();
  }

  private toggleToolbars(): void {
    const toolbarBase = document.querySelector('[data-kt-shipping-line-table-toolbar="base"]');
    const toolbarSelected = document.querySelector('[data-kt-shipping-line-table-toolbar="selected"]');

    if (this.selectedShippingLines.length > 0) {
      toolbarBase?.classList.add('d-none');
      toolbarSelected?.classList.remove('d-none');
    } else {
      toolbarBase?.classList.remove('d-none');
      toolbarSelected?.classList.add('d-none');
    }
  }

  deleteSelectedShippingLines(): void {
    if (this.selectedShippingLines.length === 0) {
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete ${this.selectedShippingLines.length} selected shipping line(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {
    if (this.selectedShippingLines.length === 0) {
      return;
    }

    this.subscriptions.push(
      this._shippingLineService.BulkDelete(this.selectedShippingLines).subscribe({
        next: () => {
          this.loadShippingLines();
          this.selectedShippingLines = [];
          this.toggleToolbars();
          Swal.fire('Deleted!', 'Selected shipping lines have been deleted.', 'success');
        },
        error: (error) => {
          console.error('Error deleting shipping lines:', error);
          Swal.fire('Error!', 'Failed to delete selected shipping lines.', 'error');
        }
      })
    );
  }

  // Math utility for template
  Math = Math;
}
